import { useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * Port of VersionCheckService. CodeBuild overwrites public/assets/version.json
 * on deploy, so a running tab notices it is stale and can offer a refresh.
 */
export function useVersionCheck(): { updateAvailable: boolean } {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let currentVersion: string | null = null;
    let cancelled = false;

    const readVersion = async (cacheBust: boolean): Promise<string | null> => {
      try {
        const url = cacheBust ? `/assets/version.json?t=${Date.now()}` : '/assets/version.json';
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) return null;
        const body = (await response.json()) as { version?: string };
        return body.version ?? null;
      } catch {
        return null; // offline or mid-deploy; try again next tick
      }
    };

    void readVersion(false).then((version) => {
      if (!cancelled) currentVersion = version;
    });

    const intervalId = setInterval(async () => {
      const latest = await readVersion(true);
      if (cancelled || !latest || !currentVersion) return;
      if (latest !== currentVersion) {
        setUpdateAvailable(true);
        clearInterval(intervalId);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  return { updateAvailable };
}
